import {
  MutationHookOptions,
  MutationTuple,
  useMutation as _useMutation,
  ApolloError,
  OperationVariables,
  TypedDocumentNode,
} from "@apollo/client"
import * as Sentry from "@sentry/browser"
import { useAPIError } from "../APIError"
import { config } from "../config"
import type { DocumentNode } from "graphql"
import { getApolloErrorExplanation } from "./getApolloErrorExplanation"

export const segmentTrack = (eventName: string, properties = {}) => {
  try {
    // @ts-expect-error
    analytics.track(eventName, {
      eventSource: "client_side",
      ...properties,
    })
  } catch (err) {
    if (config.isProductionEnvironment) Sentry.captureException(err)
  }
}

function sendErrorToSentry(query: DocumentNode, error: ApolloError) {
  const queryNameRegExp = /mutation ([^(]*)/i
  const mutationName = (query?.loc?.source?.body || "").match(queryNameRegExp)?.[0]
  Sentry.captureMessage(`Error on ${mutationName}\n${error}`)
}

export type InjectedOptions<
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
  TError = string[],
> = MutationHookOptions<TData, TVariables> & {
  handlePayloadErrors?: boolean
  analyticsEventName?: string | null
  setPayloadErrorsHook?: (params: TError) => void
}

export const useMutation = <
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
  TError = string[],
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: InjectedOptions<TData, TVariables, TError>,
): MutationTuple<TData, TVariables> => {
  const { addError } = useAPIError()
  const setPayloadErrorsHook = options?.setPayloadErrorsHook

  const [f, { error, data, ...rest }] = _useMutation(query, {
    ...options,
    onError: (error, clientOptions) => {
      if (error) {
        addError(getApolloErrorExplanation(error))
        sendErrorToSentry(query, error)
      }
      options?.onError?.(error, clientOptions)
    },
    onCompleted: (data, ...rest) => {
      if (data) {
        const mutationPayload = Object.values(data)[0]
        const mutationErrors = (mutationPayload as any)?.errors as string[]
        const hasErrors = mutationErrors?.length > 0

        if (hasErrors) {
          sendErrorToSentry(query, mutationErrors as unknown as ApolloError)
          if (options?.handlePayloadErrors) {
            // Show errors instead of "Oops" message
            addError(mutationErrors.join(", "))
          } else if (setPayloadErrorsHook) {
            // Use state hook in the calling component
            setPayloadErrorsHook(mutationErrors as unknown as TError)
          } else {
            console.error(mutationErrors.join("\n"), query.loc?.source.body)
          }
        }
        if (mutationErrors?.length === 0 && !error) {
          try {
            const eventName = options?.analyticsEventName
            if (eventName) {
              segmentTrack(eventName)
            }
          } catch (err) {
            Sentry.captureException(err)
          }
        }
      }

      options?.onCompleted?.(data, ...rest)
    },
  })

  return [f, { error, data, ...rest }]
}
